/**
 * Composable for managing scroll lock on the document body.
 *
 * This composable uses a global counter to track how many modals are open,
 * ensuring that scroll is only restored when ALL modals are closed.
 * This prevents issues with nested modals where closing one would
 * prematurely restore scrolling.
 *
 * Usage:
 * ```js
 * import { useScrollLock } from '@/composables/ui/useScrollLock'
 *
 * // In your modal component:
 * const { lockScroll, unlockScroll } = useScrollLock()
 *
 * watch(() => props.open, (isOpen) => {
 *   if (isOpen) {
 *     lockScroll()
 *   } else {
 *     unlockScroll()
 *   }
 * })
 * ```
 */

import { onUnmounted, ref } from 'vue'

// Global counter for tracking open modals
// Using a module-level variable ensures all components share the same counter
let openModalCount = 0
let savedOverflow = ''

export function useScrollLock() {
  // Track if this instance has locked scroll (to prevent double unlock)
  const hasLocked = ref(false)

  const lockScroll = () => {
    if (hasLocked.value) return // Already locked by this instance

    if (openModalCount === 0) {
      // Save the current overflow value before changing it
      savedOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'
    }
    openModalCount++
    hasLocked.value = true
  }

  const unlockScroll = () => {
    if (!hasLocked.value) return // Not locked by this instance

    openModalCount--
    hasLocked.value = false

    if (openModalCount <= 0) {
      openModalCount = 0 // Safety: prevent negative count
      document.body.style.overflow = savedOverflow
    }
  }

  // Cleanup on component unmount to prevent memory leaks
  // and ensure scroll is restored if component is destroyed while modal is open
  onUnmounted(() => {
    if (hasLocked.value) {
      unlockScroll()
    }
  })

  return {
    lockScroll,
    unlockScroll,
    hasLocked
  }
}
